/* POVA — UI sound effects (SFX)
 *
 * Subtle, synthesized interaction cues for KEY CONTROLS ONLY — CTAs, finish
 * swatches, the theme toggle, nav open/close, add-to-cart, spec/buy pills.
 * Deliberate actions get a cue; ordinary links stay silent, so the layer
 * reads as considered rather than noisy.
 *
 * GLOBAL ON/OFF: this engine is gated entirely on the site's existing sound
 * switch. The .mhdr sound button (wired in commerce-chrome / body-index)
 * toggles `body.sound-on` and persists it to localStorage['pova-audio'] across
 * every page. We simply check that class at click time, so SFX follow the same
 * single, persistent global state as the product-video audio — no second
 * setting, no separate persistence to keep in sync.
 *
 * Everything is generated with the Web Audio API (no audio files to download),
 * through one shared, lazily-created AudioContext that is only ever resumed
 * inside a real user gesture (a click), which keeps it within browser autoplay
 * policy. If Web Audio is unavailable the whole thing no-ops silently.
 *
 * Exposes window.POVA_SFX = { pop, cue, enabled } so the sound toggle can share
 * this one AudioContext for its own "sound turned on" pop.
 */
(function () {
  "use strict";

  var AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) { window.POVA_SFX = { pop: function () {}, cue: function () {}, enabled: function () { return false; } }; return; }

  var ctx = null;
  var master = null;

  function ensureCtx() {
    try {
      if (!ctx) {
        ctx = new AC();
        master = ctx.createGain();
        master.gain.value = 0.9;              /* headroom — individual cues stay quiet */
        master.connect(ctx.destination);
      }
      if (ctx.state === "suspended") ctx.resume();
    } catch (e) { ctx = null; }
    return ctx;
  }

  /* Global gate — the site's one persistent sound state. */
  function enabled() {
    return document.body && document.body.classList.contains("sound-on");
  }

  /* One synth voice: a short osc → gain envelope. Params tuned per cue.
   * f0→f1 pitch glide, peak gain, duration (s), and wave shape. */
  function voice(f0, f1, peak, dur, type) {
    if (!ensureCtx()) return;
    try {
      var t = ctx.currentTime;
      var osc = ctx.createOscillator();
      var g = ctx.createGain();
      osc.type = type || "triangle";
      osc.frequency.setValueAtTime(f0, t);
      if (f1 && f1 !== f0) osc.frequency.exponentialRampToValueAtTime(f1, t + dur * 0.9);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(peak, t + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      osc.connect(g); g.connect(master);
      osc.start(t); osc.stop(t + dur + 0.02);
    } catch (e) {}
  }

  /* Cue palette — kept quiet and short so the layer feels precise, not busy. */
  var CUES = {
    /* generic key control */
    tick: function () { voice(560, 380, 0.05, 0.055, "triangle"); },
    /* finish swatch / radio — crisper + higher */
    tap:  function () { voice(880, 700, 0.045, 0.04, "sine"); },
    /* primary action: CTA / add to cart / see-all — a small up-chirp "confirm" */
    cta:  function () { voice(300, 560, 0.09, 0.10, "triangle"); },
    /* nav open/close, pill + drawer links — soft + low */
    nav:  function () { voice(420, 300, 0.055, 0.06, "sine"); },
    /* toggle (theme, and the sound button's own "on") — the house pop */
    pop:  function () { voice(440, 170, 0.16, 0.15, "triangle"); }
  };

  function cue(name) {
    if (!enabled()) return;
    var fn = CUES[name] || CUES.tick;
    fn();
  }

  /* Resolve a clicked element to a cue — KEY CONTROLS ONLY. Anything that
   * doesn't match a key control returns null and stays silent. Ordered by
   * specificity; first match wins. */
  function resolve(el) {
    if (!el || !el.closest) return null;
    /* The sound button owns its own pop (on enable only), so never double it. */
    if (el.closest(".mhdr-sound")) return null;
    if (el.closest('.swatch, [role="radio"]')) return "tap";
    if (el.closest('.cta, .cta-link, .cta--soon, .buy-pill, .spec-pill, .lineage-all, .scroll-cue, .buy-options__option, [data-reload]')) return "cta";
    if (el.closest('.mhdr-btn[aria-label="Theme"]')) return "pop";
    if (el.closest('.nav-pill a, .pill-link, .burger, .nav-drawer a, .nav-drawer-close, .glass.cart, .cart-chip, .back-link, .wordmark')) return "nav";
    /* remaining deliberate controls: header buttons, buy-bar, spec disclosure */
    if (el.closest('button.mhdr-btn, .buy-bar button, summary')) return "tick";
    return null;
  }

  /* Capture phase so a cue still fires even if a handler stops propagation. */
  document.addEventListener("click", function (e) {
    if (!enabled()) return;
    var name = resolve(e.target);
    if (name) cue(name);
  }, true);

  window.POVA_SFX = { pop: CUES.pop, cue: cue, enabled: enabled };
})();
